import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listUploadRequests, requestUpload } from '../services/observabilityAdminApi';

export const ACTIVE_UPLOAD_STATUSES = ['pending', 'in_progress', 'collecting', 'uploading'];
export const TERMINAL_UPLOAD_STATUSES = ['completed', 'partial', 'failed', 'expired', 'cancelled'];
export const COLLECTION_LOCK_STATUSES = ['pending', 'collecting'];
export const COLLECTION_ACTIVE_STATUSES = ['pending', 'collecting', 'uploading'];

export function toEpochMs(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const asNumber = Number(value);
    if (Number.isFinite(asNumber) && String(value).trim() !== '' && !String(value).includes('T')) {
        return asNumber;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
}

/** ends_at, or starts_at/created_at + duration_ms when the hub has not stamped ends_at yet. */
export function resolveSessionEndsAt(job) {
    if (!job) return null;
    const endsAt = toEpochMs(job.ends_at);
    if (endsAt != null) return endsAt;
    const start = toEpochMs(job.starts_at) ?? toEpochMs(job.created_at);
    const duration = Number(job.duration_ms);
    if (start != null && Number.isFinite(duration) && duration > 0) {
        return start + duration;
    }
    return null;
}

export function isCollectionSessionLocking(job, hub, jobs) {
    const list = Array.isArray(jobs) ? jobs : (job ? [job] : []);
    if (list.some((item) => item?.type === 'collection_session' && COLLECTION_LOCK_STATUSES.includes(item.status))) {
        return true;
    }
    return hub?.collection_session_active === true;
}

export function isCollectionSessionActive(job, hub, jobs) {
    const list = Array.isArray(jobs) ? jobs : (job ? [job] : []);
    if (list.some((item) => item?.type === 'collection_session' && COLLECTION_ACTIVE_STATUSES.includes(item.status))) {
        return true;
    }
    return hub?.collection_session_active === true;
}

const POLL_INTERVAL_MS = 5000;

export function jobIncludesSource(job, source) {
    return Array.isArray(job?.sources) && job.sources.includes(source);
}

export function jobIncludesTelemetrySources(job) {
    return jobIncludesSource(job, 'resource_utilization') || jobIncludesSource(job, 'ai_telemetry');
}

function pickPrimaryJob(jobs) {
    if (!Array.isArray(jobs) || jobs.length === 0) return null;
    return jobs.find((item) => ACTIVE_UPLOAD_STATUSES.includes(item.status)) || jobs[0];
}

/**
 * List + create hub upload jobs. Polls the list while any job is non-terminal.
 */
export function useUploadRequest(hubId, session, { onTerminal } = {}) {
    const [jobs, setJobs] = useState([]);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [listLoading, setListLoading] = useState(false);

    const onTerminalRef = useRef(onTerminal);
    onTerminalRef.current = onTerminal;

    const intervalRef = useRef(null);
    const prevStatusesRef = useRef({});
    const sessionRef = useRef(session);
    sessionRef.current = session;

    const job = useMemo(() => pickPrimaryJob(jobs), [jobs]);
    const isActive = jobs.some((item) => ACTIVE_UPLOAD_STATUSES.includes(item.status));

    const stopPolling = useCallback(() => {
        if (intervalRef.current != null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const applyJobs = useCallback((nextJobs) => {
        const items = Array.isArray(nextJobs) ? nextJobs : [];
        const prev = prevStatusesRef.current;
        const nextStatuses = {};
        items.forEach((item) => {
            nextStatuses[item.job_id] = item.status;
            const wasActive = ACTIVE_UPLOAD_STATUSES.includes(prev[item.job_id]);
            const nowTerminal = TERMINAL_UPLOAD_STATUSES.includes(item.status);
            if (wasActive && nowTerminal && typeof onTerminalRef.current === 'function') {
                onTerminalRef.current(item);
            }
        });
        prevStatusesRef.current = nextStatuses;
        setJobs(items);
    }, []);

    const refreshList = useCallback(async () => {
        const currentSession = sessionRef.current;
        if (!hubId || !currentSession?.accessToken) return;

        const result = await listUploadRequests(hubId, currentSession);
        if (!result.ok) {
            setError(result.body?.error || `Failed to load upload requests (${result.status}).`);
            return;
        }
        setError(null);
        applyJobs(result.body?.items || []);
    }, [hubId, applyJobs]);

    const startPolling = useCallback(() => {
        stopPolling();
        refreshList();
        intervalRef.current = setInterval(() => {
            refreshList();
        }, POLL_INTERVAL_MS);
    }, [refreshList, stopPolling]);

    const submit = useCallback(async (body) => {
        if (!hubId || !sessionRef.current?.accessToken) {
            return { ok: false, status: 0, error: 'Not signed in.' };
        }

        setSubmitting(true);
        setError(null);
        try {
            const result = await requestUpload(hubId, body, sessionRef.current);
            if (!result.ok) {
                const message = mapUploadRequestError(result);
                return { ok: false, status: result.status, error: message, body: result.body };
            }

            const created = result.body || {};
            await refreshList();
            if (ACTIVE_UPLOAD_STATUSES.includes(created.status || 'pending')) {
                startPolling();
            }
            return { ok: true, status: result.status, job: created };
        } catch (err) {
            const message = err?.message || 'Failed to submit upload request.';
            return { ok: false, status: 0, error: message };
        } finally {
            setSubmitting(false);
        }
    }, [hubId, refreshList, startPolling]);

    useEffect(() => {
        if (!hubId || !session?.accessToken) return undefined;

        let cancelled = false;
        setListLoading(true);
        refreshList().finally(() => {
            if (!cancelled) setListLoading(false);
        });

        return () => {
            cancelled = true;
            stopPolling();
        };
    }, [hubId, session?.accessToken, refreshList, stopPolling]);

    useEffect(() => {
        if (isActive) {
            if (intervalRef.current == null) {
                startPolling();
            }
            return undefined;
        }
        stopPolling();
        return undefined;
    }, [isActive, startPolling, stopPolling]);

    return { job, jobs, isActive, error, submitting, listLoading, submit, refreshList };
}

export function mapUploadRequestError(result) {
    if (result?.status === 429) {
        return 'Too many pending uploads. Wait for one to finish.';
    }
    if (result?.status === 409) {
        return 'A collection session is already running for this hub.';
    }
    if (result?.body?.error) {
        return String(result.body.error);
    }
    if (result?.status) {
        return `Request failed (${result.status}).`;
    }
    return 'Request failed.';
}
